import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, ShieldOff, ExternalLink, CheckCircle2, AlertTriangle, Play } from "lucide-react";
import { mailApi, fmtDate } from "./mailApi";

const METHOD = { one_click: "disiscritto (1 click)", mailto: "disiscritto (via e-mail)", link: "apri link per disiscriverti", none: "nessun link di disiscrizione" };

// Blocked senders: future mail goes to Trash automatically every 15 minutes.
export function MailRules({ onChanged }) {
  const [rows, setRows] = useState(null);
  const [applying, setApplying] = useState(false);
  const load = () => mailApi.rules().then(setRows).catch(() => toast.error("Errore caricamento"));
  useEffect(() => { load(); }, []);

  const remove = async (em) => {
    if (!window.confirm(`Rimuovere la regola per ${em}? Le prossime mail resteranno in Posta in arrivo.`)) return;
    await mailApi.deleteRule(em); load(); onChanged?.();
  };
  const applyNow = async () => {
    setApplying(true);
    try { const r = await mailApi.applyRulesNow(); toast.success(`${r.moved} mail spostate nel Cestino`); load(); onChanged?.(); }
    catch { toast.error("Non riuscito: controlla la connessione Gmail"); }
    finally { setApplying(false); }
  };

  if (!rows) return <div className="py-8 text-center"><Loader2 className="h-5 w-5 animate-spin inline text-[#4a1c17]" /></div>;
  if (rows.length === 0) return <p className="py-8 text-center text-sm text-[#7c5a4e]" data-testid="mail-rules-empty">Nessuna regola. Usa "Blocca" su una newsletter o conferma un'etichetta per aggiungerla qui.</p>;
  return (
    <div className="rounded-xl border border-[#e8dcc8] overflow-hidden" data-testid="mail-rules-list">
      <p className="px-3 py-2 bg-[#faf3ee] text-xs text-[#7c5a4e]">Regole automatiche (ogni 15 minuti): i mittenti bloccati vanno nel Cestino, gli altri nell'etichetta indicata.</p>
      {rows.map((r) => {
        const u = r.unsubscribe || {};
        if (r.builtin) return (
          <div key="builtin-bot-forms" className="flex items-center gap-3 px-3 py-2.5 border-t border-[#f0e6d8] bg-amber-50/40" data-testid="mail-rule-bot-forms">
            <div className="min-w-0 flex-1">
              <div className="text-sm font-semibold text-[#2b1410] truncate">{r.email} <span className="ml-1 text-[10px] uppercase tracking-wide font-bold text-red-700 bg-red-50 rounded px-1.5 py-0.5">cestino</span> <span className="ml-1 text-[10px] uppercase tracking-wide font-bold text-amber-800 bg-amber-100 rounded px-1.5 py-0.5">filtro anti-bot</span></div>
              <div className="text-[11px] text-[#9a7f72] flex flex-wrap items-center gap-x-2">
                <span>{r.note}</span>
                <span>· {r.applied_count || 0} mail spostate</span>
                {r.last_applied_at && <span>· ultimo {fmtDate(r.last_applied_at)}</span>}
              </div>
            </div>
            <button onClick={applyNow} disabled={applying} className="inline-flex items-center gap-1 rounded-full border border-[#e8dcc8] px-3 py-1 text-xs text-[#4a1c17] hover:bg-[#f7ebdf] disabled:opacity-50" data-testid="mail-rules-apply-now">{applying ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Play className="h-3.5 w-3.5" />} Applica ora</button>
          </div>
        );
        return (
          <div key={r.email} className="flex items-center gap-3 px-3 py-2.5 border-t border-[#f0e6d8] bg-white" data-testid={`mail-rule-${r.email}`}>
            <div className="min-w-0 flex-1">
              <div className="text-sm font-semibold text-[#2b1410] truncate">{r.email} {r.action === "label" ? <span className="ml-1 text-[10px] uppercase tracking-wide font-bold text-sky-800 bg-sky-50 rounded px-1.5 py-0.5">→ {r.label}</span> : <span className="ml-1 text-[10px] uppercase tracking-wide font-bold text-red-700 bg-red-50 rounded px-1.5 py-0.5">cestino</span>}</div>
              <div className="text-[11px] text-[#9a7f72] flex flex-wrap items-center gap-x-2">
                <span>dal {fmtDate(r.created_at)}</span>
                <span>· {r.applied_count || 0} mail spostate</span>
                {r.action !== "label" && <span className={`inline-flex items-center gap-1 ${u.ok ? "text-emerald-700" : "text-amber-700"}`}>
                  {u.ok ? <CheckCircle2 className="h-3 w-3" /> : <AlertTriangle className="h-3 w-3" />} {METHOD[u.method] || METHOD.none}
                </span>}
                {u.link && !u.ok && <a href={u.link} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 underline text-[#4a1c17]" data-testid={`mail-rule-unsub-${r.email}`}><ExternalLink className="h-3 w-3" /> link</a>}
              </div>
            </div>
            <button onClick={() => remove(r.email)} className="inline-flex items-center gap-1 rounded-full border border-[#e8dcc8] px-3 py-1 text-xs text-[#4a1c17] hover:bg-[#f7ebdf]" data-testid={`mail-rule-remove-${r.email}`}><ShieldOff className="h-3.5 w-3.5" /> Rimuovi</button>
          </div>
        );
      })}
    </div>
  );
}
